// src/ImageUploader.js
import React, { useState, useRef } from 'react';
import { CloudUpload, Delete } from '@mui/icons-material';

const styles = {
    container: {
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    previewContainer: {
        position: 'relative',
        width: '100%',
        height: '150px',
        background: '#f0f0f0',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
    },
    previewImage: {
        width: '100%',
        height: '100%',
        objectFit: 'contain'
    },
    removeButton: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        background: 'rgba(0,0,0,0.6)',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    fileInput: {
        display: 'none',
    },
    label: {
        fontWeight: 600,
        color: '#444',
        fontSize: 14
    },
    uploadButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '10px 16px',
        border: '1px dashed #007bff',
        color: '#007bff',
        background: '#f8f9fa',
        borderRadius: '4px',
        cursor: 'pointer',
        textAlign: 'center'
    },
    actions: {
        display: 'flex',
        gap: '10px',
        alignItems: 'center'
    },
    confirmUploadButton: {
        padding: '8px 16px',
        border: 'none',
        borderRadius: '4px',
        background: '#28a745',
        color: 'white',
        cursor: 'pointer',
        flexGrow: 1
    },
    cancelButton: {
        padding: '8px 16px',
        border: 'none',
        borderRadius: '4px',
        background: '#dc3545',
        color: 'white',
        cursor: 'pointer'
    },
    error: {
        color: '#dc3545',
        fontSize: 12,
        margin: '0'
    },
    loading: {
        fontStyle: 'italic',
        color: '#555'
    }
};

const TOLERANCE = 30

export default function ImageUploader({
    label,
    name,
    value,
    onUrlChange,
    requiredWidth,
    requiredHeight
}) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setError('');
        const reader = new FileReader();
        reader.onloadend = () => {
            const img = new Image();
            img.onload = () => {
                if (Math.abs(img.width - requiredWidth) > TOLERANCE ||
                    Math.abs(img.height - requiredHeight) > TOLERANCE) {
                    setError(`Invalid dimensions. Required: ${requiredWidth}x${requiredHeight}px.`);
                    setSelectedFile(null);
                    setPreviewUrl(null);
                } else {
                    setSelectedFile(file);
                    setPreviewUrl(reader.result);
                }

            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a valid file first.');
            return;
        }
        setLoading(true);
        setError('');

        const formData = new FormData();
        formData.append('image', selectedFile);

        try {
            // NOTE: Using '/api/upload' as a relative path. Adjust if your API setup is different.
            const response = await fetch('https://dev9.careercarve.in/api/test/uploadContent', {
                method: 'PUT',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Upload failed with status: ${response.status}`);
            }

            const result = await response.json();

            console.log(result)
            const fullPath = result.image_url;
            onUrlChange(name, fullPath);

            // Clear the selection after successful upload
            setSelectedFile(null);
            setPreviewUrl(null);

        } catch (err) {
            setError(err.message || 'An error occurred during upload.');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveImage = () => {
        onUrlChange(name, '');
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div style={styles.container}>
            <label style={styles.label}>{label} ({requiredWidth}x{requiredHeight}px)</label>
            {(value || previewUrl) && (
                <div style={styles.previewContainer}>
                    <img
                        src={previewUrl || value}
                        alt="Preview"
                        style={styles.previewImage}
                    />
                    {value && !previewUrl && (
                        <button type="button" style={styles.removeButton} onClick={handleRemoveImage} title="Remove existing image">
                            <Delete fontSize="small" />
                        </button>
                    )}
                </div>
            )}

            <input
                type="file"
                accept="image/png, image/jpeg, image/webp"
                ref={fileInputRef}
                style={styles.fileInput}
                onChange={handleFileChange}
            />

            {!selectedFile && (
                <button type="button" style={styles.uploadButton} onClick={() => fileInputRef.current.click()}>
                    <CloudUpload fontSize="small" />
                    Choose File...
                </button>
            )}

            {selectedFile && (
                <div style={styles.actions}>
                    <button type="button" style={styles.confirmUploadButton} onClick={handleUpload} disabled={loading}>
                        {loading ? 'Uploading...' : 'Confirm & Upload'}
                    </button>
                    <button type="button" style={styles.cancelButton} onClick={handleCancel} disabled={loading}>
                        Cancel
                    </button>
                </div>
            )}

            {error && <p style={styles.error}>{error}</p>}
        </div>
    );
}